import { useState, type CSSProperties } from "react";

import { describeError } from "../app/useResource";
import { formatDuration, formatIsoTime } from "../app/format";
import { useBackend } from "../app/BackendProvider";
import { useNetworkId } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import type {
  SortieReallocationMatch,
  SortieReallocationTarget,
  SortieReallocationVehicle,
} from "../contract/types";
import { usePolledResource } from "./hooks";
import { LineBadge } from "./bits";
import type { ToolsScope } from "./toolsScope";

const CLASS_LABEL: Record<SortieReallocationTarget["requiredClass"], string> = {
  standard: "standard",
  articulated: "articulé",
  metro: "métro",
};

const PHASE_LABEL: Record<SortieReallocationVehicle["phase"], string> = {
  "ending-soon": "dernière course en cours",
  hlp: "haut-le-pied",
  returning: "HLP retour dépôt",
  "at-depot": "rentré",
};

const OUTCOME_LABEL: Record<SortieReallocationMatch["outcome"], string> = {
  full: "reprise complète",
  partial: "reprise partielle",
  none: "aucune reprise",
};

const FEASIBILITY_LABEL: Record<SortieReallocationMatch["feasibility"], string> = {
  feasible: "réalisable",
  uncertain: "incertain",
};

const sectionTitle: CSSProperties = { margin: "0 0 6px", color: "var(--sentinelle-ink-3)", fontSize: "0.72rem" };
const rowStyle: CSSProperties = {
  border: "1px solid var(--sentinelle-rule)",
  background: "#fff",
  borderRadius: 4,
  padding: "7px 10px",
};

/**
 * Outil « Répartition des sorties » (contrat v1.45, dump utilisateur
 * 2026-09-10) — quand une sortie doit être « déshabillée » (véhicule en panne
 * ou retiré avant sa Rentrée), aide à trouver un véhicule bientôt libre pour
 * reprendre ses courses restantes, en respectant matériel/aptitude/dépôt.
 *
 * Balayage : sorties à découvert + pool de donneurs + suggestions classées.
 * Cliquer une sortie bascule en mode ciblé (`?target=`) : tous les donneurs
 * classés pour elle, avec les incompatibilités signalées en rouge.
 * CONSULTATIF : aucun bouton « Affecter » — Sentinelle informe, n'affecte pas.
 *
 * Portée « véhicule » : lit le balayage et n'en garde que ce qui touche CE
 * véhicule — sa sortie est-elle à découvert (et qui pourrait la reprendre) ?
 * peut-il lui-même reprendre une sortie à découvert ?
 */
export function SortieReallocationPanel({ scope }: { scope: ToolsScope }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const postParam = useActivePostParam();
  const [pickedRunId, setTargetRunId] = useState<string | null>(null);
  const vehicleRunId = scope.kind === "vehicle" ? (scope.journey.runId ?? null) : null;
  // Portée véhicule : toujours le balayage (on en extrait ce qui concerne le véhicule),
  // jamais le mode ciblé choisi à la main.
  const targetRunId = scope.kind === "vehicle" ? null : pickedRunId;

  const { data, error, loading } = usePolledResource(
    (signal) =>
      backend.api.getSortieReallocation(
        networkId,
        {
          ...(postParam ? { post: postParam } : {}),
          ...(targetRunId ? { target: targetRunId } : {}),
        },
        signal,
      ),
    [backend, networkId, postParam, targetRunId],
    20_000,
  );

  if (loading && !data) return <p className="has-text-grey is-size-7">Chargement…</p>;
  if (error) return <p className="has-text-danger is-size-7">{describeError(error)}</p>;
  if (!data) return null;

  if (scope.kind === "vehicle") {
    return <VehicleView runId={vehicleRunId} targets={data.targets} donors={data.donors} matches={data.matches} />;
  }

  if (targetRunId) {
    if (!data.target) {
      // La sortie s'est résorbée (couverte, ou n'existe plus) entre-temps.
      return (
        <div style={{ display: "grid", gap: 8 }}>
          <BackButton onClick={() => setTargetRunId(null)} />
          <p className="has-text-grey is-size-7">Cette sortie n'est plus à découvert.</p>
        </div>
      );
    }
    return (
      <TargetedView
        target={data.target}
        donors={data.donors}
        matches={data.matches}
        onBack={() => setTargetRunId(null)}
      />
    );
  }

  return (
    <SweepView targets={data.targets} donors={data.donors} matches={data.matches} onPickTarget={setTargetRunId} />
  );
}

/** Ce que la répartition des sorties dit d'UN véhicule (portée « véhicule »). */
function VehicleView({
  runId,
  targets,
  donors,
  matches,
}: {
  runId: string | null;
  targets: SortieReallocationTarget[];
  donors: SortieReallocationVehicle[];
  matches: SortieReallocationMatch[];
}) {
  if (!runId) {
    return (
      <p className="has-text-grey is-size-7">
        La sortie de ce véhicule n'est pas encore rapprochée : la répartition des sorties ne peut pas le situer.
      </p>
    );
  }
  const target = targets.find((t) => t.runId === runId) ?? null;
  const covering = matches.filter((m) => m.targetRunId === runId);
  const canTake = matches.filter((m) => m.donorRunId === runId);

  if (!target && canTake.length === 0) {
    return (
      <p className="has-text-grey is-size-7">
        Rien à répartir pour ce véhicule : sa sortie {runId} est couverte et il ne figure pas parmi les
        donneurs possibles d'une sortie à découvert.
      </p>
    );
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {target ? (
        <section>
          <p className="ovl" style={sectionTitle}>
            Sa sortie est à découvert
          </p>
          <div style={{ display: "grid", gap: 6 }}>
            <TargetRow target={target} />
            {covering.length === 0 ? (
              <p className="has-text-grey is-size-7">Aucun donneur compatible pour l'instant.</p>
            ) : (
              covering.map((m, i) => (
                <MatchRow key={`${m.donorRunId}-${i}`} match={m} donors={donors} showTarget={false} />
              ))
            )}
          </div>
        </section>
      ) : null}
      {canTake.length > 0 ? (
        <section>
          <p className="ovl" style={sectionTitle}>
            Peut reprendre une sortie à découvert
          </p>
          <div style={{ display: "grid", gap: 6 }}>
            {canTake.map((m, i) => (
              <MatchRow key={`${m.targetRunId}-${i}`} match={m} donors={donors} showTarget />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="is-size-7"
      style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--sentinelle-accent, #254fb6)" }}
    >
      ← Retour au balayage
    </button>
  );
}

function SweepView({
  targets,
  donors,
  matches,
  onPickTarget,
}: {
  targets: SortieReallocationTarget[];
  donors: SortieReallocationVehicle[];
  matches: SortieReallocationMatch[];
  onPickTarget: (runId: string) => void;
}) {
  if (targets.length === 0 && donors.length === 0) {
    return <p className="has-text-grey is-size-7">Rien à signaler.</p>;
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <section>
        <p className="ovl" style={sectionTitle}>
          Sorties à découvert
        </p>
        {targets.length === 0 ? (
          <p className="has-text-grey is-size-7">Aucune sortie à découvert.</p>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {targets.map((t) => (
              <TargetRow key={t.runId} target={t} onClick={() => onPickTarget(t.runId)} />
            ))}
          </div>
        )}
      </section>

      <section>
        <p className="ovl" style={sectionTitle}>
          Véhicules bientôt libres
        </p>
        {donors.length === 0 ? (
          <p className="has-text-grey is-size-7">Aucun donneur disponible.</p>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {donors.map((d) => (
              <DonorRow key={d.runId} donor={d} />
            ))}
          </div>
        )}
      </section>

      <section>
        <p className="ovl" style={sectionTitle}>
          Suggestions
        </p>
        {matches.length === 0 ? (
          <p className="has-text-grey is-size-7">Aucune suggestion.</p>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {matches.map((m, i) => (
              <MatchRow key={`${m.donorRunId}-${m.targetRunId}-${i}`} match={m} donors={donors} showTarget />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TargetedView({
  target,
  donors,
  matches,
  onBack,
}: {
  target: SortieReallocationTarget;
  donors: SortieReallocationVehicle[];
  matches: SortieReallocationMatch[];
  onBack: () => void;
}) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <BackButton onClick={onBack} />
      <TargetRow target={target} />
      <section>
        <p className="ovl" style={sectionTitle}>
          Donneurs classés pour cette sortie
        </p>
        {matches.length === 0 ? (
          <p className="has-text-grey is-size-7">Aucun donneur candidat.</p>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {matches.map((m, i) => (
              <MatchRow key={`${m.donorRunId}-${i}`} match={m} donors={donors} showTarget={false} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TargetRow({ target, onClick }: { target: SortieReallocationTarget; onClick?: () => void }) {
  const content = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <LineBadge lineId={target.lineId} size={20} />
        <span style={{ fontWeight: 700 }}>{target.sortie}</span>
        <span className="has-text-grey is-size-7" style={{ marginInlineStart: "auto" }}>
          {CLASS_LABEL[target.requiredClass]}
          {target.isTeorLine ? " · TEOR" : ""}
        </span>
      </div>
      <p className="is-size-7" style={{ margin: "3px 0 0" }}>
        Prochain départ <strong>{formatIsoTime(target.nextCourseDepartsAt)}</strong> de {target.nextCourseFromStopName}
      </p>
      <p className="has-text-grey is-size-7" style={{ margin: "2px 0 0" }}>
        {target.remainingCoursesCount} course{target.remainingCoursesCount > 1 ? "s" : ""} restante
        {target.remainingCoursesCount > 1 ? "s" : ""} · {formatDuration(target.remainingServiceSeconds)} de service
        {target.rentreeAt ? ` · Rentrée ${formatIsoTime(target.rentreeAt)}` : ""}
        {target.depot ? ` · ${target.depot.label}` : ""}
      </p>
    </>
  );
  if (!onClick) return <div style={rowStyle}>{content}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...rowStyle, display: "block", width: "100%", textAlign: "left", cursor: "pointer" }}
    >
      {content}
    </button>
  );
}

function DonorRow({ donor }: { donor: SortieReallocationVehicle }) {
  return (
    <div style={rowStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <LineBadge lineId={donor.lineId} size={20} />
        <span style={{ fontWeight: 700 }}>{donor.sortie}</span>
        {donor.vehicle ? <span className="mono has-text-grey is-size-7">n° {donor.vehicle.displayNumber}</span> : null}
        <span className="has-text-grey is-size-7" style={{ marginInlineStart: "auto" }}>
          {CLASS_LABEL[donor.vehicleType.class]}
        </span>
      </div>
      <p className="is-size-7" style={{ margin: "3px 0 0" }}>
        Libre à <strong>{formatIsoTime(donor.freeAt)}</strong>
        {donor.freeAtStopName ? ` · ${donor.freeAtStopName}` : ""}
      </p>
      <p className="has-text-grey is-size-7" style={{ margin: "2px 0 0" }}>
        {PHASE_LABEL[donor.phase]}
        {donor.depot ? ` · ${donor.depot.label}` : ""}
      </p>
    </div>
  );
}

function MatchRow({
  match,
  donors,
  showTarget,
}: {
  match: SortieReallocationMatch;
  donors: SortieReallocationVehicle[];
  showTarget: boolean;
}) {
  const donor = donors.find((d) => d.runId === match.donorRunId);
  const outcomeColor =
    match.outcome === "full" ? "#15803d" : match.outcome === "partial" ? "#b7950b" : "var(--sentinelle-ink-3)";
  return (
    <div style={rowStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700 }}>{donor?.sortie ?? match.donorRunId}</span>
        {showTarget ? (
          <>
            <span className="has-text-grey">→</span>
            <span style={{ fontWeight: 700 }}>{match.targetRunId}</span>
          </>
        ) : null}
        <span className="is-size-7" style={{ color: outcomeColor, marginInlineStart: "auto" }}>
          {OUTCOME_LABEL[match.outcome]} · {FEASIBILITY_LABEL[match.feasibility]}
        </span>
      </div>
      <p className="is-size-7" style={{ margin: "3px 0 0" }}>
        {match.hlpSeconds != null ? (
          <>
            HLP {formatDuration(match.hlpSeconds)} · prêt à <strong>{formatIsoTime(match.readyAt)}</strong>
          </>
        ) : (
          "Pas de trajet HLP calculable"
        )}
        {match.catchesCourseDepartsAt ? (
          <>
            {" "}
            → rejoint le départ de <strong>{formatIsoTime(match.catchesCourseDepartsAt)}</strong>
          </>
        ) : null}
      </p>
      <p className="has-text-grey is-size-7" style={{ margin: "2px 0 0" }}>
        {match.coursesCoveredCount} course{match.coursesCoveredCount > 1 ? "s" : ""} couverte
        {match.coursesCoveredCount > 1 ? "s" : ""}
        {match.coursesMissedCount > 0
          ? ` · ${match.coursesMissedCount} non couverte${match.coursesMissedCount > 1 ? "s" : ""}`
          : ""}
      </p>
      {!match.classCompatible || !match.aptitudeOk ? (
        <p className="has-text-danger is-size-7" style={{ margin: "2px 0 0", fontWeight: 600 }}>
          {!match.classCompatible ? "Matériel incompatible" : ""}
          {!match.classCompatible && !match.aptitudeOk ? " · " : ""}
          {!match.aptitudeOk ? "Aptitude insuffisante" : ""}
        </p>
      ) : null}
      {match.sameDepot === false ? <p className="has-text-grey is-size-7" style={{ margin: "2px 0 0" }}>Dépôt différent</p> : null}
    </div>
  );
}
